import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../firebase';
import StorageIndicator from './StorageIndicator';
import ApiKeysModal from './ApiKeysModal';
import '../../css/Navbar.css';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';
const BLUEBIRD_DOCS_URL = 'https://bluebird-documentation.com';
// Poll cadence for notifications. Deliberately wide, and gated on tab visibility below —
// tight always-on polling kept the serverless database from ever scaling to zero.
const POLL_INTERVAL = 120000;

const NOTIFICATION_TYPE_LABELS = {
    comment: 'comment',
    document_created: 'document',
    document_shared: 'document',
    document_updated: 'document',
    user_subscription: 'subscription',
    workspace_subscription: 'subscription',
    workspace_management: 'workspace',
    mention: 'mention',
    reminder: 'reminder',
    transcription_complete: 'transcription',
    transcription_failed: 'transcription',
    task_assigned: 'task',
    board_invited: 'board',
    task_updated: 'task',
    task_comment: 'task',
    comment_reply: 'task',
    board_role_changed: 'board',
};

const filterByPrefs = (list, prefs) => {
    if (!prefs) return list;
    return list.filter(n => {
        switch (n.notification_type) {
            case 'comment':                 return prefs.commentNotifications !== false;
            case 'document_created':
            case 'document_shared':
            case 'document_updated':        return prefs.documentSharedNotifications !== false;
            case 'user_subscription':
            case 'workspace_subscription':  return prefs.subscriptionNotifications !== false;
            case 'workspace_management':    return prefs.workspaceNotifications !== false;
            case 'mention':                 return prefs.mentionNotifications !== false;
            case 'reminder':                return prefs.reminderNotifications !== false;
            case 'task_assigned':           return prefs.taskAssignmentNotifications !== false;
            case 'board_invited':           return prefs.taskBoardInviteNotifications !== false;
            case 'task_updated':
            case 'task_comment':
            case 'comment_reply':           return prefs.taskUpdateNotifications !== false;
            case 'board_role_changed':      return prefs.taskBoardRoleNotifications !== false;
            default:                        return true;
        }
    });
};

const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
};

const Navbar = () => {
    const { user, apiCall, getToken, appendSSOCode, warmSSOCode } = useAuth();
    const navigate = useNavigate();

    // Bump to re-render once an on-demand SSO code lands, so the suite links
    // (built synchronously via appendSSOCode) pick it up before the click.
    const [, setSsoTick] = useState(0);
    const warmSSO = useCallback(() => {
        Promise.resolve(warmSSOCode()).then(() => setSsoTick(t => t + 1));
    }, [warmSSOCode]);

    // ─── User menu ───────────────────────────────────────────────────
    const [menuOpen, setMenuOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [apiKeysOpen, setApiKeysOpen] = useState(false);
    const [isLightTheme, setIsLightTheme] = useState(
        () => document.documentElement.getAttribute('data-theme') === 'light'
    );
    const menuRef = useRef(null);

    // ─── Notification center ─────────────────────────────────────────
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifPrefs, setNotifPrefs] = useState(null);
    const [seenIds, setSeenIds] = useState(new Set());
    const notifRef = useRef(null);
    const pollRef = useRef(null);

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        try {
            const res = await apiCall('/notifications/all/', {
                headers: { userUid: user.uid },
            });
            if (res.ok) {
                const data = await res.json();
                const all = data.notifications || [];
                setNotifications(filterByPrefs(all, notifPrefs));
            }
        } catch {
            // silently fail — notifications are non-critical
        }
    }, [user, notifPrefs, apiCall]);

    const fetchNotifPrefs = useCallback(async () => {
        if (!user) return;
        try {
            const token = await getToken();
            const res = await apiCall(`/users/${user.uid}/notification-preferences/`, {
                headers: { 'Auth-Token': `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setNotifPrefs(data.preferences || null);
            }
        } catch {
            // silently fail
        }
    }, [user, apiCall, getToken]);

    // Fetch preferences once, then start polling for notifications
    useEffect(() => {
        if (!user) return;
        fetchNotifPrefs();
    }, [user, fetchNotifPrefs]);

    useEffect(() => {
        if (!user) return;
        fetchNotifications();
        const tick = () => { if (!document.hidden) fetchNotifications(); };
        pollRef.current = setInterval(tick, POLL_INTERVAL);
        const onVisibilityChange = () => { if (!document.hidden) fetchNotifications(); };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            clearInterval(pollRef.current);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [user, fetchNotifications]);

    const toggleNotifDropdown = () => {
        const opening = !notifOpen;
        setNotifOpen(opening);
        if (opening) {
            // The overflow footer links to Account Settings with SSO; only warm
            // a code when that link will actually render.
            if (notifications.length > 10) warmSSO();
            // Mark all currently visible as seen
            setSeenIds(prev => {
                const next = new Set(prev);
                notifications.forEach(n => next.add(n.id));
                return next;
            });
        }
    };

    const deleteNotification = async (notifId, e) => {
        e.stopPropagation();
        try {
            await apiCall(`/notifications/${notifId}/delete/`, { method: 'DELETE' });
            setNotifications(prev => prev.filter(n => n.id !== notifId));
        } catch {
            // silently fail
        }
    };

    const clearAllNotifications = async (e) => {
        e.stopPropagation();
        if (!user) return;
        try {
            await apiCall('/notifications/delete-all/', {
                method: 'DELETE',
                headers: { userUid: user.uid },
            });
            setNotifications([]);
        } catch {
            // silently fail
        }
    };

    const handleNotifClick = (notification, e) => {
        const url = notification.related_url;
        if (!url) return;

        setNotifOpen(false);

        if (e.button === 1 || e.ctrlKey || e.metaKey) {
            // Open in new tab
            const fullUrl = url.startsWith('/board/')
                ? `${window.location.origin}${url}`
                : `${BLUEBIRD_DOCS_URL}${url}`;
            window.open(fullUrl, '_blank', 'noopener,noreferrer');
        } else if (url.startsWith('/board/')) {
            navigate(url);
        } else {
            window.open(`${BLUEBIRD_DOCS_URL}${url}`, '_blank', 'noopener,noreferrer');
        }
    };

    const unreadCount = notifications.filter(n => !seenIds.has(n.id)).length;

    // ─── Theme toggle ────────────────────────────────────────────────
    const handleToggleTheme = () => {
        const next = !isLightTheme;
        setIsLightTheme(next);
        if (next) {
            document.documentElement.setAttribute('data-theme', 'light');
            localStorage.setItem('bb-theme', 'light');
        } else {
            document.documentElement.removeAttribute('data-theme');
            localStorage.removeItem('bb-theme');
        }
    };

    const handleLogout = async () => {
        setLoggingOut(true);
        try {
            await logout();
            navigate('/login');
        } catch (err) {
            console.error('Error logging out:', err);
            setLoggingOut(false);
        }
    };

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
            if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
        };
        if (menuOpen || notifOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen, notifOpen]);

    return (
        <nav className="Navbar">
            <div className="Navbar__left">
                <h2 className="Navbar__brand" onClick={() => navigate('/')}>
                    Bluebird <span className="Navbar__brand-sub">Tasks</span>
                </h2>
            </div>
            <div className="Navbar__right">
                <button
                    className="Navbar__theme-toggle"
                    onClick={handleToggleTheme}
                    title={isLightTheme ? 'Switch to dark theme' : 'Switch to light theme'}
                    aria-label="Toggle theme"
                >
                    <i className={`fas ${isLightTheme ? 'fa-moon' : 'fa-sun'}`} />
                </button>
                {user && (
                    <>
                        <StorageIndicator />

                        {/* Notification Center */}
                        <div className="Navbar__notif-container" ref={notifRef}>
                            <button
                                className="Navbar__notif-btn"
                                onClick={toggleNotifDropdown}
                                title="Notifications"
                                aria-label="Notifications"
                                aria-expanded={notifOpen}
                            >
                                <i className="fas fa-bell" />
                                {unreadCount > 0 && (
                                    <span className="Navbar__notif-badge">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                )}
                            </button>

                            {notifOpen && (
                                <div className="Navbar__notif-dropdown">
                                    <div className="Navbar__notif-header">
                                        <span className="Navbar__notif-title">Notifications</span>
                                        {notifications.length > 0 && (
                                            <div className="Navbar__notif-header-actions">
                                                <span className="Navbar__notif-total">
                                                    {notifications.length} total
                                                </span>
                                                <button
                                                    className="Navbar__notif-clear-all"
                                                    onClick={clearAllNotifications}
                                                    title="Clear all notifications"
                                                >
                                                    Clear all
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="Navbar__notif-list">
                                        {notifications.length === 0 ? (
                                            <div className="Navbar__notif-empty">
                                                <i className="fas fa-bell-slash" />
                                                <span>No notifications</span>
                                            </div>
                                        ) : (
                                            notifications.slice(0, 10).map(notification => {
                                                const isUnread = !seenIds.has(notification.id);
                                                const isClickable = !!notification.related_url;
                                                const typeLabel = NOTIFICATION_TYPE_LABELS[notification.notification_type] || '';
                                                return (
                                                    <div
                                                        key={notification.id}
                                                        className={`Navbar__notif-item${isUnread ? ' Navbar__notif-item--unread' : ''}${isClickable ? ' Navbar__notif-item--clickable' : ''}`}
                                                        onClick={isClickable ? (e) => handleNotifClick(notification, e) : undefined}
                                                        onMouseDown={isClickable ? (e) => { if (e.button === 1) { e.preventDefault(); handleNotifClick(notification, e); } } : undefined}
                                                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                                                    >
                                                        <div className="Navbar__notif-content">
                                                            <div className="Navbar__notif-item-title">
                                                                {notification.title}
                                                            </div>
                                                            <div className="Navbar__notif-item-message">
                                                                {notification.message}
                                                            </div>
                                                            <div className="Navbar__notif-item-meta">
                                                                <span className="Navbar__notif-time">
                                                                    {formatTimeAgo(notification.created_at)}
                                                                </span>
                                                                {typeLabel && (
                                                                    <span className="Navbar__notif-type">
                                                                        {typeLabel}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button
                                                            className="Navbar__notif-delete"
                                                            onClick={(e) => deleteNotification(notification.id, e)}
                                                            title="Dismiss"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {notifications.length > 10 && (
                                        <div className="Navbar__notif-footer">
                                            <span className="Navbar__notif-overflow">
                                                +{notifications.length - 10} more — manage in{' '}
                                                <a href={appendSSOCode(`${BLUEBIRD_DOCS_URL}/account/${user.uid}`)} target="_blank" rel="noopener noreferrer">
                                                    Account Settings
                                                </a>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <span className="Navbar__email">{user.email}</span>
                        <div className="Navbar__user-menu" ref={menuRef}>
                            <button
                                className="Navbar__user-btn"
                                onClick={() => {
                                    if (!menuOpen) warmSSO(); // menu holds the SSO'd Account Settings link
                                    setMenuOpen((prev) => !prev);
                                }}
                                aria-expanded={menuOpen}
                                aria-haspopup="true"
                            >
                                <i className="fas fa-user-circle" />
                                <i className={`fas fa-chevron-down Navbar__chevron${menuOpen ? ' Navbar__chevron--open' : ''}`} />
                            </button>
                            {menuOpen && (
                                <div className="Navbar__dropdown">
                                    <a
                                        className="Navbar__dropdown-item"
                                        href={appendSSOCode(`${BLUEBIRD_DOCS_URL}/account/${user.uid}`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <i className="fas fa-sliders-h" />
                                        <span>Account Settings</span>
                                    </a>
                                    <button
                                        className="Navbar__dropdown-item"
                                        onClick={() => { setMenuOpen(false); setApiKeysOpen(true); }}
                                    >
                                        <i className="fas fa-key" />
                                        <span>API Keys</span>
                                    </button>
                                    <div className="Navbar__dropdown-divider" />
                                    <button
                                        className="Navbar__dropdown-item Navbar__dropdown-item--danger"
                                        onClick={() => { setMenuOpen(false); handleLogout(); }}
                                        disabled={loggingOut}
                                    >
                                        {loggingOut
                                            ? <><span className="btn-spinner btn-spinner--danger" /><span>Signing out...</span></>
                                            : <><i className="fas fa-sign-out-alt" /><span>Sign Out</span></>}
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {apiKeysOpen && <ApiKeysModal onClose={() => setApiKeysOpen(false)} />}
        </nav>
    );
};

export default Navbar;
